import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Leaf, Calendar, ListTodo, Sprout, Clock, Brain, CheckCircle2, ArrowRight, ChevronDown, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { LegalFooter } from '@/components/LegalFooter';
import { SEO } from '@/components/SEO';
import gardenBgLogin from '@/assets/garden-bg-login.webp';
import screenshotDashboard from '@/assets/screenshot-dashboard.webp';
import screenshotProgress from '@/assets/screenshot-progress.jpg';
import screenshotPlants from '@/assets/screenshot-plants.jpg';


const FEATURES = [
  {
    icon: Brain,
    title: 'Offload the Mental Load',
    description: 'Stop trying to remember what to sow, when to plant out, and what needs harvesting. We handle the thinking.',
  },
  {
    icon: Calendar,
    title: 'Personalised Schedule',
    description: 'Get a month-by-month calendar tailored to your plants, your setup, and your growing zone.',
  },
  {
    icon: Clock,
    title: 'Fits Your Life',
    description: 'Tell us how much time you have — weekdays and weekends — and we\'ll plan around your schedule.',
  },
  {
    icon: ListTodo,
    title: 'Simple Task Lists',
    description: 'Each day shows exactly what needs doing. Just check tasks off as you go — no guesswork.',
  },
  {
    icon: Sprout,
    title: 'Sowing to Harvest',
    description: 'Track every plant from seed sowing through hardening off, planting out, and harvest.',
  },
  {
    icon: CheckCircle2,
    title: 'Progress at a Glance',
    description: 'See how each plant is progressing through its lifecycle — never lose track of where you are.',
  },
];

const TESTIMONIALS = [
  {
    quote: "I used to spend more time planning my garden than actually being in it. Now I just open the app, see what needs doing today, and get on with it.",
    name: "Sarah",
    detail: "Allotment grower, 12 plants",
  },
  {
    quote: "Finally something that works around my schedule instead of expecting me to become a full-time gardener.",
    name: "James",
    detail: "Balcony garden, 6 plants",
  },
  {
    quote: "The scheduling is brilliant — I didn't realise how much brain space gardening was taking up until I stopped having to plan it all.",
    name: "Priya",
    detail: "Raised beds, 15 plants",
  },
];

/** Get a seasonal hook message based on current month */
function getSeasonalHook(): { badge: string; hook: string } {
  const month = new Date().getMonth(); // 0-indexed
  if (month >= 2 && month <= 4) return { badge: '🌱 Spring is here!', hook: 'April is prime sowing time — don\'t miss your window.' };
  if (month >= 5 && month <= 7) return { badge: '☀️ Summer growing', hook: 'Your plants need attention now — stay on track effortlessly.' };
  if (month >= 8 && month <= 9) return { badge: '🍂 Autumn harvest', hook: 'Harvest season is here — know exactly what\'s ready to pick.' };
  return { badge: '❄️ Plan ahead', hook: 'Get your garden plan ready so you\'re first to sow when spring arrives.' };
}

export default function Landing() {
  const navigate = useNavigate();
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const seasonal = getSeasonalHook();

  const screenshots = [
    { src: screenshotDashboard, alt: 'Plan My Garden dashboard with monthly calendar and task list', label: 'Smart Calendar' },
    { src: screenshotProgress, alt: 'Plant progress tracking showing lifecycle stages', label: 'Progress Tracking' },
    { src: screenshotPlants, alt: 'Plant selection screen with variety of vegetables and flowers', label: 'Plant Library' },
  ];

  return (
    <div className="min-h-screen bg-background" role="main">
      <SEO
        title="Plan My Garden — Your Personal Gardening Assistant"
        description="Stop keeping it all in your head. Plan My Garden builds a personalised schedule so you know exactly what to sow, plant, and harvest — and when."
        path="/"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Plan My Garden',
            url: 'https://planmygarden.app/',
            description: 'A personal gardening assistant that builds a tailored sowing, planting, and harvesting schedule for home growers.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Plan My Garden',
            url: 'https://planmygarden.app/',
          },
        ]}
      />
      {/* Nav */}

      <nav className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-8">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl garden-gradient shadow-soft">
              <Leaf className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-sm font-bold text-foreground tracking-tight">Plan My Garden</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Button variant="ghost" size="sm" className="rounded-xl text-xs" onClick={() => navigate('/pricing')}>
              Pricing
            </Button>
            <Button variant="ghost" size="sm" className="rounded-xl text-xs hidden sm:inline-flex" onClick={() => navigate('/about')}>
              About
            </Button>
            <Button size="sm" className="rounded-xl garden-gradient text-xs shadow-soft" onClick={() => navigate('/auth')}>
              Sign In
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero — compact, urgency-driven, CTA visible on mobile without scrolling */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <img
            src={gardenBgLogin}
            alt=""
            fetchPriority="high"
            className="absolute bottom-0 left-0 w-full h-auto opacity-40 object-cover object-bottom"
            style={{ minHeight: '50%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background" />
        </div>

        <div className="relative mx-auto max-w-4xl px-4 pt-10 pb-8 sm:pt-16 sm:pb-14 text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            {/* Seasonal urgency badge */}
            <Badge variant="secondary" className="mb-3 rounded-full px-3 py-1 text-xs">
              {seasonal.badge}
            </Badge>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight leading-tight">
              Your garden,{' '}
              <span className="text-primary">planned for you</span>
            </h1>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
              {seasonal.hook} Plan My Garden builds a personalised schedule so you know exactly what to do and when.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                size="lg"
                className="rounded-xl garden-gradient shadow-soft text-base font-semibold px-8 h-12 transition-all hover:shadow-medium hover:scale-[1.01] active:scale-[0.99] w-full sm:w-auto"
                onClick={() => navigate('/auth')}
              >
                Start Free — Takes 3 Minutes <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {/* Inline social proof — right below CTA */}
            <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3 w-3 fill-garden-sun text-garden-sun" />
                ))}
              </div>
              <span>Free for up to 5 plants · No credit card needed</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product preview — immediately after hero, shows value fast */}
      <section className="pb-8 sm:pb-12 -mt-2">
        <div className="mx-auto max-w-5xl px-4">
          {/* Screenshot tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-4 px-2">
            {screenshots.map((s, i) => (
              <button
                key={i}
                onClick={() => setActiveScreenshot(i)}
                className={`px-3 py-1.5 rounded-lg text-[11px] sm:text-sm font-medium whitespace-nowrap transition-all ${
                  activeScreenshot === i
                    ? 'bg-primary text-primary-foreground shadow-soft'
                    : 'bg-card text-muted-foreground hover:bg-accent/20'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <motion.div
            key={activeScreenshot}
            initial={{ opacity: 0, scale: 0.99 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="rounded-2xl overflow-hidden shadow-lifted border border-border/40 bg-card"
          >
            <img
              src={screenshots[activeScreenshot].src}
              alt={screenshots[activeScreenshot].alt}
              className="w-full h-auto"
              width={1200}
              height={800}
            />
          </motion.div>
        </div>
      </section>

      {/* Quick 3-step "how it works" — scannable in seconds */}
      <section className="py-10 sm:py-14">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight text-center mb-6">
            Up and running in 3 minutes
          </h2>

          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { step: '1', title: 'Pick your plants', desc: 'Veg, herbs, flowers — whatever you want to grow.' },
              { step: '2', title: 'Set your time', desc: 'Tell us how much time you have. We fit around you.' },
              { step: '3', title: 'Follow the plan', desc: 'Daily tasks, check them off. No guesswork.' },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.08 }}
                className="text-center p-4"
              >
                <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full garden-gradient text-primary-foreground font-bold shadow-soft mb-3">
                  {item.step}
                </div>
                <h3 className="font-semibold text-foreground text-sm">{item.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain point — empathy hook */}
      <section className="py-10 sm:py-14 bg-muted/30">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-xl sm:text-2xl font-bold text-foreground leading-snug tracking-tight">
              "When did I sow those tomatoes? Should I plant out the beans yet? Did I miss the window for lettuce?"
            </p>
            <p className="mt-3 text-muted-foreground text-base">
              Sound familiar? You love gardening, but keeping track of it all takes up more headspace than it should.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-10 sm:py-14">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight text-center mb-8">
            How Plan My Garden helps
          </h2>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
              >
                <Card className="h-full border-border/40 shadow-soft hover:shadow-medium transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 mb-3">
                      <feature.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10 sm:py-14 bg-muted/30">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight text-center mb-6">
            What gardeners say
          </h2>

          <div className="grid gap-4 sm:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.08 }}
              >
                <Card className="h-full border-border/40 shadow-soft">
                  <CardContent className="pt-6">
                    <p className="text-sm text-foreground italic leading-relaxed">"{t.quote}"</p>
                    <div className="mt-3 pt-3 border-t border-border/40">
                      <p className="text-sm font-medium text-foreground">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.detail}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 sm:py-18 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <img
            src={gardenBgLogin}
            alt=""
            className="absolute bottom-0 left-0 w-full h-auto opacity-30 object-cover object-bottom"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background" />
        </div>

        <div className="relative mx-auto max-w-2xl px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              Ready to garden without the stress?
            </h2>
            <p className="mt-3 text-muted-foreground text-base">
              Free for up to 5 plants. No credit card needed.
            </p>
            <Button
              size="lg"
              className="mt-5 rounded-xl garden-gradient shadow-soft text-base font-semibold px-8 h-12 transition-all hover:shadow-medium hover:scale-[1.01] active:scale-[0.99] w-full sm:w-auto"
              onClick={() => navigate('/auth')}
            >
              Start Growing Smarter <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </motion.div>
        </div>
      </section>

      <LegalFooter fixed={false} />
    </div>
  );
}
